import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Brain, Clock, Filter, Loader2, Network, Pencil, Plus, RefreshCw, Search, Trash2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { useToast } from "@/shared/components/ui/use-toast";
import { DataPagination } from "@/shared/components/DataPagination";
import { usePagination } from "@/shared/hooks/usePagination";
import { formatDateBR } from "@/shared/utils/datetime";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { MemoryService } from "../services/memoryService";
import {
  MEMORY_KINDS,
  MEMORY_STATUSES,
  type Memory,
  type MemoryInput,
  type MemoryKind,
  type MemoryStatus,
  type MemoryTarget,
} from "../types/MemoryTypes";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { useIsAdmin } from "@/shared/hooks/use-admin-validation";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { authorLabel, filterMemories, moveScopes, validityState } from "../utils/memoryView";
import { MemoryFactsSheet } from "./MemoryFactsSheet";
import { MemoryFormDialog } from "./MemoryFormDialog";

interface MemoryPanelProps {
  target: MemoryTarget;
  /** Quem pode gravar neste nível. Editar e esquecer vem de cada memória (`can_edit`). */
  canCreate: boolean;
}

const VALIDITY_VARIANT = {
  permanent: "outline",
  valid: "outline",
  expiringSoon: "warning",
  expired: "destructive",
} as const;

/**
 * A Memory de um nível — workspace (aba do Brain), pessoa (My view) ou organização
 * (AB#87149). A mesma tela nos três; quem pode o quê vem da api.
 */
export const MemoryPanel = ({ target, canCreate }: MemoryPanelProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ["memories", target.scope, target.productId ?? "-"];

  const { data: memories = [], isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => MemoryService.list(target),
    // Recém-gravada fica `pending` até entrar no grafo: atualiza sozinha enquanto houver uma.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((memory: Memory) => memory.status === "pending") ? 5000 : false,
  });

  const [query, setQuery] = useState("");
  const [statuses, setStatuses] = useState<MemoryStatus[]>([]);
  const [kinds, setKinds] = useState<MemoryKind[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const filtered = useMemo(
    () => filterMemories(memories, { query, kinds, statuses }),
    [memories, query, kinds, statuses],
  );
  const activeFilters = statuses.length + kinds.length;
  const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData } = usePagination<Memory>();

  const [form, setForm] = useState<{ open: boolean; memory: Memory | null; renew: boolean }>({
    open: false,
    memory: null,
    renew: false,
  });
  const [saving, setSaving] = useState(false);
  const [forgetting, setForgetting] = useState<Memory | null>(null);
  const [forgetBusy, setForgetBusy] = useState(false);
  const [factsOf, setFactsOf] = useState<Memory | null>(null);

  // Mover uma memória tira de uma lista e põe em outra: atualiza todas as listas de memória.
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["memories"] });

  const { currentWorkspace } = useWorkspace();
  const { isAdmin } = useIsAdmin();
  const { isSuperAdmin } = useIsSuperAdminRole();
  const workspaceId = target.productId ?? currentWorkspace?.id ?? null;
  const scopes = form.memory
    ? moveScopes(form.memory.scope, { workspaceId, isAdmin: isAdmin || isSuperAdmin })
    : [];

  const save = async (input: MemoryInput) => {
    setSaving(true);
    try {
      if (form.memory) {
        await MemoryService.update(target, form.memory.id, input, workspaceId);
        toast({ title: t("memory.form.updated") });
      } else {
        await MemoryService.create(target, input);
        toast({ title: t("memory.form.created") });
      }
      setForm({ open: false, memory: null, renew: false });
      await refresh();
    } catch (error) {
      toast({
        title: t("memory.form.saveError"),
        description: resolveApiErrorMessage(error, t),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const forget = async () => {
    if (!forgetting) return;
    setForgetBusy(true);
    try {
      await MemoryService.remove(target, forgetting.id);
      toast({ title: t("memory.forget.done") });
      setForgetting(null);
      await refresh();
    } catch (error) {
      toast({
        title: t("memory.forget.error"),
        description: resolveApiErrorMessage(error, t),
        variant: "destructive",
      });
    } finally {
      setForgetBusy(false);
    }
  };

  const openCreate = () => setForm({ open: true, memory: null, renew: false });

  function toggle<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
  }

  return (
    <Card>
      <div className="space-y-4 p-4">
        {/* Header — o mesmo desenho da Mind: busca e filtro à esquerda, ações à direita. */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 z-10 h-4 w-4 text-muted-foreground" />
              <Input
                className="w-[250px] pl-8"
                placeholder={t("memory.search")}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <div className="relative">
                  <Button variant="outline" size="icon" aria-label={t("memory.filters")}>
                    <Filter className="h-4 w-4" />
                  </Button>
                  {activeFilters > 0 && (
                    <Badge
                      variant="secondary"
                      className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full p-0"
                    >
                      {activeFilters}
                    </Badge>
                  )}
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="start">
                <div className="flex items-center justify-between border-b px-3 py-2">
                  <h3 className="font-medium">{t("memory.filters")}</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setStatuses([]);
                      setKinds([]);
                      setCurrentPage(1);
                    }}
                  >
                    {t("memory.clear")}
                  </Button>
                </div>
                <div className="space-y-4 p-4">
                  {[
                    { title: t("memory.filterStatus"), ids: MEMORY_STATUSES, prefix: "status", selected: statuses as string[] },
                    { title: t("memory.filterKind"), ids: MEMORY_KINDS, prefix: "kinds", selected: kinds as string[] },
                  ].map((group) => (
                    <div key={group.prefix} className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">{group.title}</p>
                      {group.ids.map((id) => (
                        <div key={id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`memory-${group.prefix}-${id}`}
                            checked={group.selected.includes(id)}
                            onCheckedChange={() => {
                              if (group.prefix === "status") setStatuses((current) => toggle(current, id as MemoryStatus));
                              else setKinds((current) => toggle(current, id as MemoryKind));
                              setCurrentPage(1);
                            }}
                          />
                          <Label htmlFor={`memory-${group.prefix}-${id}`} className="cursor-pointer text-sm font-normal">
                            {t(`memory.${group.prefix}.${id}`)}
                          </Label>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {canCreate && (
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              {t("memory.add")}
            </Button>
          )}
        </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <p className="py-6 text-sm text-destructive">{t("memory.loadError")}</p>
      ) : memories.length === 0 ? (
        <div className="flex h-[calc(100vh-280px)] flex-col items-center justify-center gap-8 rounded-lg border-2 border-dashed">
          <div className="flex flex-col items-center gap-2">
            <Brain className="h-24 w-24 text-muted-foreground/40" />
            <p className="max-w-md text-center text-sm text-muted-foreground">{t("memory.empty")}</p>
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={openCreate}
              className="group flex w-44 cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed p-6 transition-colors hover:border-primary hover:bg-primary/5"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted group-hover:bg-primary/10">
                <Plus className="h-5 w-5 text-muted-foreground group-hover:text-primary" />
              </div>
              <p className="text-sm font-medium">{t("memory.add")}</p>
            </button>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">{t("memory.noResults")}</p>
      ) : (
        <>
          <ul className="space-y-2">
            {paginateData(filtered).map((memory) => {
              const validity = validityState(memory);
              const author = authorLabel(memory);
              const date = memory.expires_at ? formatDateBR(memory.expires_at) : "";
              return (
                <li key={memory.id} className="rounded-md border p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <p className="min-w-0 flex-1 whitespace-pre-wrap text-sm">{memory.content}</p>
                    <div className="flex shrink-0 items-center gap-1">
                      {memory.status === "active" && (
                        <Button size="sm" variant="ghost" onClick={() => setFactsOf(memory)} title={t("memory.actions.facts")}>
                          <Network className="h-4 w-4" />
                        </Button>
                      )}
                      {memory.can_edit && validity === "expired" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setForm({ open: true, memory, renew: true })}
                          title={t("memory.actions.renew")}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}
                      {memory.can_edit && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setForm({ open: true, memory, renew: false })}
                            title={t("memory.actions.edit")}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setForgetting(memory)}
                            title={t("memory.actions.forget")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="secondary" className="text-[10px]">
                      {t(`memory.kinds.${memory.kind}`)}
                    </Badge>
                    <Badge variant={VALIDITY_VARIANT[validity]} className="gap-1 text-[10px]">
                      {validity !== "permanent" && <Clock className="h-3 w-3" />}
                      {validity === "permanent"
                        ? t("memory.validity.permanent")
                        : t(`memory.validity.${validity === "valid" ? "until" : validity}`, { date })}
                    </Badge>
                    {(memory.status === "pending" || memory.status === "failed") && (
                      <Badge
                        variant={memory.status === "failed" ? "warning" : "outline"}
                        className="text-[10px]"
                        title={t(`memory.statusHint.${memory.status}`)}
                      >
                        {memory.status === "pending" && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                        {t(`memory.status.${memory.status}`)}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[10px]">
                      {t(`memory.sources.${memory.source}`)}
                    </Badge>
                    {memory.source_ref?.client && <span>{t("memory.via", { client: memory.source_ref.client })}</span>}
                    <span>
                      {memory.created_at ? formatDateBR(memory.created_at) : ""}
                      {author ? ` · ${t("memory.by", { name: author })}` : ""}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
          <DataPagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      <MemoryFormDialog
        open={form.open}
        onOpenChange={(open) => setForm((current) => ({ ...current, open }))}
        memory={form.memory}
        renew={form.renew}
        scopes={scopes}
        saving={saving}
        onSubmit={save}
      />

      <MemoryFactsSheet target={target} memory={factsOf} onOpenChange={(open) => !open && setFactsOf(null)} />

      <AlertDialog open={!!forgetting} onOpenChange={(open) => !open && !forgetBusy && setForgetting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("memory.forget.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("memory.forget.description")}</AlertDialogDescription>
          </AlertDialogHeader>
          {forgetting && (
            <blockquote className="border-l-2 pl-3 text-sm text-muted-foreground">{forgetting.content}</blockquote>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={forgetBusy}>{t("memory.forget.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={forgetBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(event) => {
                event.preventDefault();
                void forget();
              }}
            >
              {forgetBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("memory.forget.forgetting")}
                </>
              ) : (
                t("memory.forget.confirm")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </Card>
  );
};
